/**
 * PuppeteerSkillEffect — Specter phantom-path active: single-enemy cognition redirect.
 *
 * Overwrites one enemy's last-known-player-position with a false coordinate 4-6
 * tiles from the player's real position. The enemy breaks off whatever they were
 * doing and investigates the planted location. Works on suspicious or lower alert
 * states by default — Misinform removes the alert-level gate entirely.
 *
 * Upgrade hooks:
 * - Misinform (worksOnAlertedEnemies): remove alert-level restriction
 * - Double Blind (targetCount=2, cooldownMultiplier=1.3): target 2 enemies, 30% cooldown penalty
 * - Handler (revealPatrolRouteOnRedirect): set patrolRevealed=true on redirected enemies
 *
 * The false position is seeded from the player's actual position — the resolver
 * supplies the displacement offset or the targeting dep computes it.
 *
 * Implements SkillEffect directly — same DI architecture as Orchestrate/Doppelganger.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Puppeteer, Misinform, Double Blind, Handler
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PuppeteerDeps {
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
    /** Returns nearest N valid enemies sorted by distance. */
    getTargets(count: number, worksOnAlerted: boolean): PuppeteerTarget[];
    /** Computes a false position 4-6 tiles from the given real position. */
    computeFalsePosition(realX: number, realY: number): { x: number; y: number };
    getPlayerPosition(): { x: number; y: number };
  };
  readonly ai: {
    /** Instruct the enemy to investigate the given false coordinate. */
    plantFalsePosition(
      enemyId: string,
      falseX: number,
      falseY: number,
      durationMs: number,
    ): void;
    /** Sets a flag enabling permanent patrol-route visualization for this enemy. */
    revealPatrolRoute(enemyId: string): void;
  };
}

export interface PuppeteerTarget {
  readonly enemyId: string;
  readonly alertLevel: number;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PuppeteerConfig {
  readonly baseCooldownMs: number;
  readonly falseDurationMs: number;
}

const DEFAULT_CONFIG: PuppeteerConfig = {
  baseCooldownMs: 2000,       // long cooldown tier
  falseDurationMs: 6000,      // enemy investigates false position for 6 seconds
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PuppeteerSkillEffect implements SkillEffect {
  readonly skillId = 'puppeteer';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: PuppeteerDeps;
  private readonly config: PuppeteerConfig;

  private _cooldownRemaining = 0;
  private _activationCount = 0;
  private _fizzled = false;

  // Per-activation state (observable by scenarios and game layer)
  private _redirectedEnemyIds: string[] = [];
  private _patrolRevealedEnemyIds: string[] = [];
  private _resolvedTargetCount = 1;
  private _resolvedCooldownMultiplier = 1.0;
  private _worksOnAlerted = false;
  private _lastFalsePosition: { x: number; y: number } = { x: 0, y: 0 };

  constructor(deps: PuppeteerDeps, config: Partial<PuppeteerConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationCount(): number { return this._activationCount; }
  get fizzled(): boolean { return this._fizzled; }
  get redirectedEnemyIds(): readonly string[] { return this._redirectedEnemyIds; }
  get patrolRevealedEnemyIds(): readonly string[] { return this._patrolRevealedEnemyIds; }
  get resolvedTargetCount(): number { return this._resolvedTargetCount; }
  get resolvedCooldownMultiplier(): number { return this._resolvedCooldownMultiplier; }
  get worksOnAlerted(): boolean { return this._worksOnAlerted; }
  get lastFalsePosition(): { x: number; y: number } { return this._lastFalsePosition; }

  /** Effective cooldown duration, accounting for Double Blind penalty. */
  getCooldownMs(): number {
    return this.config.baseCooldownMs * this._resolvedCooldownMultiplier;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — consumes cooldown but does not redirect
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._resolvedCooldownMultiplier = this._resolveCooldownMultiplier();
      this._cooldownRemaining = this.getCooldownMs();
      return false;
    }

    // Resolve upgrade flags
    this._worksOnAlerted = this.deps.resolver.hasEffect('puppeteer', 'worksOnAlertedEnemies');
    this._resolvedTargetCount = this.deps.resolver.getMaxEffect('puppeteer', 'targetCount') ?? 1;
    this._resolvedCooldownMultiplier = this._resolveCooldownMultiplier();
    const revealPatrol = this.deps.resolver.hasEffect('puppeteer', 'revealPatrolRouteOnRedirect');

    // Get targets
    const targets = this.deps.targeting.getTargets(
      this._resolvedTargetCount,
      this._worksOnAlerted,
    );

    if (targets.length === 0) {
      // No valid targets — fizzle silently with cooldown
      this._fizzled = true;
      this._cooldownRemaining = this.getCooldownMs();
      return false;
    }

    // Compute shared false position from player's location
    const playerPos = this.deps.targeting.getPlayerPosition();
    const falsePos = this.deps.targeting.computeFalsePosition(playerPos.x, playerPos.y);
    this._lastFalsePosition = falsePos;

    this._redirectedEnemyIds = [];
    this._patrolRevealedEnemyIds = [];

    for (const target of targets) {
      this.deps.ai.plantFalsePosition(
        target.enemyId,
        falsePos.x,
        falsePos.y,
        this.config.falseDurationMs,
      );
      this._redirectedEnemyIds.push(target.enemyId);

      // Handler: permanently reveal patrol route
      if (revealPatrol) {
        this.deps.ai.revealPatrolRoute(target.enemyId);
        this._patrolRevealedEnemyIds.push(target.enemyId);
      }
    }

    // Start cooldown
    this._cooldownRemaining = this.getCooldownMs();
    this._activationCount++;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // --- Private ---

  private _resolveCooldownMultiplier(): number {
    return this.deps.resolver.getMaxEffect('puppeteer', 'cooldownMultiplier') ?? 1.0;
  }
}
